import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { IconButton, Fade } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Copyright from "../../components/Copyright";
import { useTranslation } from "react-i18next";
import FarmAppLogo from "../../assets/logo/FarmAppLogo.png";
import BackButtonIcon from "../../assets/Icon/BackButtonIcon.png";
import { saveAuth } from "../../globals/authentication";
import { SERVER_BASE_ADDRESS } from "../../globals/const";
import axios from "axios";
import { useAlert } from "../../components/Alert";
import RoleSelectionStep from "../../components/login/RoleSelectionStep";
import PasswordStep from "../../components/login/PasswordStep";

const Login = () => {
  const [isOtpLogin, setIsOtpLogin] = useState(false);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [selectedRole, setSelectedRole] = useState(null);
  const [step, setStep] = useState(1);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { showAlert } = useAlert();
  const location = useLocation();
  const { appuserData } = location.state || { appuserData: [] };

  useEffect(() => {
    if (appuserData && appuserData.length === 1) {
      setSelectedRole(appuserData[0]);
      setStep(2);
    }
  }, [appuserData]);

  const handleRoleSelect = (roleData) => {
    setSelectedRole(roleData);
  };

  const handleNextStep = () => {
    if (selectedRole) {
      setStep(2);
    }
  };

  const handleBackToRoleSelection = () => {
    setStep(1);
    setError("");
  };

  const handleTogglePasswordMode = () => {
    setIsOtpLogin(false);
    setOtp("");
    setError("");
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    if (error) setError("");
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleOtpRequest = () => {
    setLoading(true);
    setError("");

    axios
      .post(`${SERVER_BASE_ADDRESS}/auth/login`, {
        appuser_id: selectedRole.id,
        send_otp: true,
      })
      .then(() => {
        setIsOtpLogin(true);
        showAlert("success", t("OTP sent successfully"));
      })
      .catch((err) => {
        const errorMsg =
          err.response?.data?.non_field_errors?.[0] || t("Failed to send OTP.");
        setError(errorMsg);
        showAlert("error", errorMsg);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleOtpLogin = (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");

    if (!otp) {
      setError(t("Please enter OTP"));
      showAlert("error", t("Please enter OTP"));
      setLoading(false);
      return;
    }

    axios
      .post(`${SERVER_BASE_ADDRESS}/auth/login`, {
        appuser_id: selectedRole.id,
        otp: otp,
      })
      .then((result) => {
        if (result?.data) {
          saveAuth(result.data);
          showAlert("success", t("Login Successful"));
          setTimeout(() => {
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  type: "REGISTER_TOKEN",
                  token: result.data.token,
                })
              );
            }
            window.open("/", "_self");
            setLoading(false);
          }, 1000);
        }
      })
      .catch((err) => {
        let errorMsg = t("Invalid OTP");

        if (err.response?.data) {
          if (
            err.response.data.non_field_errors &&
            err.response.data.non_field_errors.length > 0
          ) {
            errorMsg = err.response.data.non_field_errors[0];
          }
        }

        setError(errorMsg);
        showAlert("error", errorMsg);
        setLoading(false);
      });
  };

  const handleOtpExpire = () => {
    setIsOtpLogin(false);
    setOtp("");
    showAlert("error", t("OTP has expired. Please request a new one."));
  };

  const login = (event) => {
    event.preventDefault();
    setError("");
    setLoading(true);

    if (!selectedRole || !password) {
      setError(t("Please enter your password"));
      showAlert("error", t("Please enter your password"));
      setLoading(false);
      return;
    }

    axios
      .post(`${SERVER_BASE_ADDRESS}/auth/login`, {
        password: password,
        appuser_id: selectedRole.id,
      })
      .then((result) => {
        if (result?.data) {
          saveAuth(result.data);
          showAlert("success", t("Login Successful"));
          if (result.data.is_new_user) {
            navigate("/reset-password");
            return;
          }
          setTimeout(() => {
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(
                JSON.stringify({
                  type: "REGISTER_TOKEN",
                  token: result.data.token,
                })
              );
            }
            window.open("/", "_self");
            setLoading(false);
          }, 1000);
        }
      })
      .catch((err) => {
        let errorMsg = t("An error occurred. Please try again.");

        if (err.response?.data) {
          if (
            err.response.data.non_field_errors &&
            err.response.data.non_field_errors.length > 0
          ) {
            errorMsg = err.response.data.non_field_errors[0];
          }
        }

        setError(errorMsg);
        showAlert("error", errorMsg);
        setLoading(false);
      });
  };

  return (
    <Container component="main" maxWidth="xs">
      <Helmet>
        <title>Login - Farmapp</title>
      </Helmet>
      <IconButton
        edge="start"
        sx={{ mx: "0.1rem", marginTop: "10px" }}
        aria-label="back"
        onClick={() => navigate(-1)}
      >
        <img src={BackButtonIcon} alt="Back" />
      </IconButton>
      <CssBaseline />
      <div
        style={{
          display: "flex",
          padding: "20px",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <img
          alt="farmapp"
          src={FarmAppLogo}
          style={{ width: "206", height: "188px" }}
        />

        <form
          style={{
            width: "100%",
            marginTop: 50,
          }}
          noValidate
          onSubmit={
            step === 2
              ? isOtpLogin
                ? handleOtpLogin
                : login
              : (e) => e.preventDefault()
          }
        >
          {step === 1 ? (
            <Fade in={step === 1}>
              <div>
                <RoleSelectionStep
                  appuserData={appuserData}
                  selectedRole={selectedRole}
                  onRoleSelect={handleRoleSelect}
                  onNext={handleNextStep}
                  t={t}
                />
              </div>
            </Fade>
          ) : (
            <Fade in={step === 2}>
              <div>
                <PasswordStep
                  selectedRole={selectedRole}
                  password={password}
                  showPassword={showPassword}
                  loading={loading}
                  onPasswordChange={handlePasswordChange}
                  onTogglePasswordVisibility={handleTogglePasswordVisibility}
                  onBack={handleBackToRoleSelection}
                  onOtpLogin={handleOtpRequest}
                  isOtpMode={isOtpLogin}
                  otp={otp}
                  onOtpChange={(value) => setOtp(value)}
                  onOtpExpire={handleOtpExpire}
                  onTogglePassword={handleTogglePasswordMode}
                  t={t}
                />
              </div>
            </Fade>
          )}
        </form>
      </div>

      <Box mt={4}>
        <Copyright />
      </Box>
    </Container>
  );
};

export default Login;
